import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Download, Copy, Check, Terminal, Monitor, RefreshCw, PackageCheck, Settings, KeyRound, List, Smartphone, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AgentDownload() {
  const [orgToken, setOrgToken]   = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [interval, setIntervalVal] = useState(60);
  const [cmdInterval, setCmdInterval] = useState(15);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [copied, setCopied]       = useState('');
  const [loading, setLoading]     = useState(true);

  // Versiones
  const [releases, setReleases]         = useState([]);
  const [assetVersions, setAssetVersions] = useState([]);
  const [activeSection, setActiveSection] = useState('install'); // install | versions | assets
  // Agente binario: se sirve desde el disco del servidor, no desde git, asi que puede
  // no estar publicado. La UI tiene que reflejar eso en vez de ofrecer una descarga rota.
  const [binInfo, setBinInfo] = useState(null);
  const [downloadingBin, setDownloadingBin] = useState(false);
  // Re-enrolamientos pendientes (anti-hijack): un equipo reinstalado intenta re-enrolar sobre
  // una credencial activa → queda acá hasta que un admin lo autorice (revoca la vigente).
  const [pendingEnrolls, setPendingEnrolls] = useState([]);

  const authToken = localStorage.getItem('token');
  const headers   = { Authorization: `Bearer ${authToken}` };

  const loadPending = useCallback(() => {
    fetch(`${API_URL}/agent/enroll-requests`, { headers })
      .then(r => (r.ok ? r.json() : [])).then(d => setPendingEnrolls(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line

  const authorizeEnroll = async (id) => {
    if (!window.confirm('¿Autorizar el re-enrolamiento? Se revoca la credencial vigente de ese equipo; el agente reinstalado obtendrá una nueva en su próximo intento.')) return;
    await fetch(`${API_URL}/agent/enroll-requests/${id}/authorize`, { method: 'POST', headers }).catch(() => {});
    loadPending();
  };
  const dismissEnroll = async (id) => {
    await fetch(`${API_URL}/agent/enroll-requests/${id}`, { method: 'DELETE', headers }).catch(() => {});
    loadPending();
  };

  const loadVersionData = useCallback(() => {
    fetch(`${API_URL}/agents/releases`, { headers })
      .then(r => r.json()).then(d => setReleases(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/agents/assets-versions`, { headers })
      .then(r => r.json()).then(d => setAssetVersions(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/agents/binary/info`, { headers })
      .then(r => (r.ok ? r.json() : null)).then(setBinInfo).catch(() => setBinInfo({ disponible: false }));
  }, []); // eslint-disable-line


  useEffect(() => {
    loadVersionData();
    loadPending();
    Promise.all([api.getAdminInfo(), api.getConfig()])
      .then(([info, cfg]) => {
        setOrgToken(info.org_token || '');
        setServerUrl(window.location.origin);
        setIntervalVal(parseInt(cfg.agent_interval_minutes?.value) || 60);
        setCmdInterval(parseInt(cfg.agent_command_interval_minutes?.value) || 15);
      })
      .catch(() => {
        setServerUrl(window.location.origin);
      })
      .finally(() => setLoading(false));
  }, []);

  const saveInterval = async () => {
    setSaving(true);
    try {
      await api.saveConfig('agent_interval_minutes', interval);
      await api.saveConfig('agent_command_interval_minutes', cmdInterval);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  // Descarga por fetch + blob: el .exe se sirve con la sesion del admin, y un <a href>
  // no puede mandar el header de autorizacion.
  const downloadBinary = async () => {
    setDownloadingBin(true);
    try {
      const r = await fetch(`${API_URL}/agents/binary/download`, { headers });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'No se pudo descargar.');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ITILAgent${binInfo?.version ? '-' + binInfo.version : ''}.exe`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloadingBin(false);
    }
  };

  const token = orgToken || 'TU_ORG_TOKEN';

  const winCmdOne = `powershell -ExecutionPolicy Bypass -Command "Invoke-WebRequest -Uri '${serverUrl}/agents/windows/install-agent.ps1' -OutFile install-agent.ps1 -UseBasicParsing; .\\install-agent.ps1 -ApiUrl '${serverUrl}' -OrgToken '${token}'"`;

  const winCmdManual = `.\\install-agent.ps1 -ApiUrl "${serverUrl}" -OrgToken "${token}"`;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="spinner" />
    </div>
  );

  // Tarjeta de una version. Extraida para poder renderizarla agrupada por canal.
  const renderRelease = (rel) => (
      <div key={rel.version} className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: rel.release_notes ? 12 : 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: rel.is_current ? 'var(--green-dim)' : 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PackageCheck size={20} style={{ color: rel.is_current ? 'var(--green)' : 'var(--ink-3)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>v{rel.version}</span>
              {rel.is_current && <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 700 }}>Actual</span>}
              {!rel.is_current && <span style={{ background: 'var(--bg)', color: 'var(--ink-2)', borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>Anterior</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{fmtDate(rel.released_at)}</div>
          </div>
          {rel.has_script && (
            <a
              href={`${API_URL}/agents/releases/${rel.version}/download`}
              download={`itil-agent-${rel.version}.ps1`}
              onClick={e => {
                e.preventDefault();
                fetch(`${API_URL}/agents/releases/${rel.version}/download`, { headers })
                  .then(r => r.blob()).then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `itil-agent-${rel.version}.ps1`; a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--ink-2)', fontSize: 12, fontWeight: 600, textDecoration: 'none', flexShrink: 0, cursor: 'pointer' }}>
              <Download size={13} />
              Descargar
            </a>
          )}
          {!rel.has_script && (
            <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>Sin script</span>
          )}
        </div>
        {rel.release_notes && (
          <pre style={{ margin: 0, fontSize: 12, color: 'var(--ink-2)', background: 'var(--bg-subtle)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6 }}>
            {rel.release_notes}
          </pre>
        )}
      </div>
  );

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Download size={22} style={{ opacity: 0.7 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Agentes</h1>
        </div>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
          Instalación, versiones y estado de los agentes en los equipos gestionados.
        </p>
      </div>

      {/* Re-enrolamientos pendientes (anti-hijack): un equipo reinstalado pide re-enrolar sobre
          una credencial activa. El admin autoriza (revoca la vigente) o descarta. */}
      {pendingEnrolls.length > 0 && (
        <div style={{ marginBottom: 24, border: '1px solid var(--amber-dim, #f0c36d)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: 'var(--amber-dim, #fff7e6)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldAlert size={18} style={{ color: 'var(--amber, #b7791f)' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>Re-enrolamientos pendientes ({pendingEnrolls.length})</div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                Estos equipos intentan re-enrolarse pero ya tienen una credencial activa. Autorizá solo si reconocés la reinstalación (revoca la credencial vigente); si no, descartá.
              </div>
            </div>
          </div>
          <div>
            {pendingEnrolls.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{p.hostname || '(sin hostname)'}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {p.attempts} intento{p.attempts === 1 ? '' : 's'} · último {fmtDate(p.last_seen)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => authorizeEnroll(p.id)}
                    style={{ padding: '7px 14px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Autorizar
                  </button>
                  <button onClick={() => dismissEnroll(p.id)}
                    style={{ padding: '7px 14px', background: 'var(--surface)', color: 'var(--ink-2)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    Descartar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs de sección */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 24, gap: 4 }}>
        {[
          { id: 'install',  label: 'Instalación',      icon: Download },
          { id: 'versions', label: 'Changelog',         icon: List,    badge: releases.length },
          { id: 'assets',   label: 'Estado por equipo', icon: Monitor, badge: assetVersions.length },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveSection(t.id)}
              style={{ padding: '10px 18px', background: 'none', border: 'none', borderBottom: activeSection === t.id ? '2px solid #6366f1' : '2px solid transparent', color: activeSection === t.id ? 'var(--accent)' : 'var(--ink-2)', fontWeight: activeSection === t.id ? 700 : 400, fontSize: 13, cursor: 'pointer', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon size={14} />
              {t.label}
              {t.badge > 0 && <span style={{ color: 'var(--ink-2)', fontSize: 11, fontWeight: 700 }}>{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* ── Sección: Changelog ── */}
      {activeSection === 'versions' && (
        <div>
          {releases.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)' }}>Sin versiones registradas</div>
          ) : (() => {
            // Dos canales distintos con numeración propia: mezclarlos por fecha daba una
            // lista donde 1.13.0 y 2.2.0 se intercalaban sin que se entendiera de qué
            // agente era cada una. Se listan agrupados, binario primero (es el actual).
            const CONOCIDOS = [
              { id: 'windows_bin', titulo: 'Agente binario', detalle: 'Servicio de Windows (.exe) — se actualiza solo' },
              { id: 'windows',     titulo: 'Agente PowerShell', detalle: 'Tareas programadas (.ps1) — en reemplazo' },
              { id: 'linux_bin',   titulo: 'Agente Linux binario', detalle: 'Binario estático (Go) — sin dependencias' },
              { id: 'linux',       titulo: 'Agente Linux Bash', detalle: 'Script (.sh)' },
            ];
            // Un canal nuevo no debe desaparecer de la vista por no figurar en la lista.
            const extras = [...new Set(releases.map(r => r.platform || 'windows'))]
              .filter(id => !CONOCIDOS.some(c => c.id === id))
              .map(id => ({ id, titulo: id, detalle: '' }));
            const canales = [...CONOCIDOS, ...extras];
            return canales.map(canal => {
              const delCanal = releases.filter(r => (r.platform || 'windows') === canal.id);
              if (delCanal.length === 0) return null;
              return (
                <div key={canal.id} style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{canal.titulo}</h3>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{canal.detalle}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
                      {delCanal.length} {delCanal.length === 1 ? 'versión' : 'versiones'}
                    </span>
                  </div>
                  {delCanal.map(rel => renderRelease(rel))}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── Sección: Estado por equipo ── */}
      {activeSection === 'assets' && (
        <div className="card">
          {assetVersions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)', fontSize: 13 }}>
              Sin equipos con agente detectado
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Equipo</th>
                    <th style={{ width: 100 }}>Versión</th>
                    <th style={{ width: 80 }}>Tipo agente</th>
                    <th style={{ width: 150 }}>Último reporte</th>
                    <th style={{ width: 150 }}>Últ. actualización</th>
                  </tr>
                </thead>
                <tbody>
                  {assetVersions.map(a => {
                    const current = releases.find(r => r.is_current);
                    const isLatest = current && a.agent_version === current.version;
                    return (
                      <tr key={a.id}>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{a.hostname}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{a.user_name}</div>
                        </td>
                        <td>
                          {a.agent_version ? (
                            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: isLatest ? 'var(--green)' : 'var(--amber)' }}>
                              v{a.agent_version}
                              {!isLatest && current && <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--amber)' }}>← v{current.version} disponible</span>}
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sin dato</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {a.agent_standard && <span style={{ fontSize: 10, color: '#0369a1', fontWeight: 600, width: 'fit-content' }}>Standard</span>}
                            {a.agent_cc && <span style={{ fontSize: 10, color: 'var(--ink-2)', fontWeight: 600, width: 'fit-content' }}>C&C</span>}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(a.last_seen)}</td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(a.last_version_change)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Sección: Instalación ── */}
      {activeSection === 'install' && (<>

      {/* Intervalo */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Settings size={17} style={{ color: 'var(--accent)' }} /> Configuración global</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ fontSize: 14, color: '#444', whiteSpace: 'nowrap', width: 180 }}>Intervalo de inventario:</label>
          <select
            className="form-input"
            style={{ width: 180 }}
            value={interval}
            onChange={e => setIntervalVal(Number(e.target.value))}
          >
            <option value={15}>Cada 15 minutos</option>
            <option value={30}>Cada 30 minutos</option>
            <option value={60}>Cada 1 hora</option>
            <option value={240}>Cada 4 horas</option>
            <option value={720}>Cada 12 horas</option>
            <option value={1440}>Una vez al día</option>
          </select>
          <span style={{ fontSize: 12, color: '#888' }}>escaneo completo del equipo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 14, color: '#444', whiteSpace: 'nowrap', width: 180 }}>Intervalo de comandos:</label>
          <select
            className="form-input"
            style={{ width: 180 }}
            value={cmdInterval}
            onChange={e => setCmdInterval(Number(e.target.value))}
          >
            <option value={5}>Cada 5 minutos</option>
            <option value={10}>Cada 10 minutos</option>
            <option value={15}>Cada 15 minutos</option>
            <option value={30}>Cada 30 minutos</option>
            <option value={60}>Cada 1 hora</option>
          </select>
          <span style={{ fontSize: 12, color: '#888' }}>ejecución de scripts / acciones remotas</span>
          <button
            className="btn btn-primary"
            onClick={saveInterval}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
          >
            {saved
              ? <><Check size={14} /> Guardado</>
              : saving
                ? <><RefreshCw size={14} /> Guardando...</>
                : 'Guardar'}
          </button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#888' }}>
          Los agentes ya instalados adoptarán los nuevos intervalos automáticamente en su próxima ejecución. El agente de comandos hace polling corto para que las remediaciones se ejecuten pronto.
        </p>
      </div>

      {/* Token */}
      <div className="card" style={{ padding: 24, marginBottom: 24, background: 'var(--amber-dim)', border: '1px solid #FDE68A' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 6 }}><KeyRound size={15} /> Token de organización</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{
            flex: 1, background: 'var(--amber-dim)', padding: '8px 12px', borderRadius: 6,
            fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all'
          }}>
            {token}
          </code>
          <button className="btn btn-secondary btn-sm" onClick={() => copy(token, 'token')} title="Copiar">
            {copied === 'token' ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--amber)' }}>
          Mantené este token seguro — autentica al agente contra tu servidor. Está incluido en los comandos de abajo.
        </p>
      </div>

      {/* Windows — agente binario (recomendado) */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <Monitor size={20} style={{ color: '#0078D4' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Windows — agente binario</h2>
          <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 700 }}>
            Recomendado
          </span>
          <span style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>
            Servicio de Windows
          </span>
          {binInfo?.version && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-2)' }}>
              v{binInfo.version}{binInfo.size ? ` · ${(binInfo.size / 1048576).toFixed(0)} MB` : ''}
            </span>
          )}
        </div>

        <p style={{ fontSize: 14, color: '#444', marginBottom: 16 }}>
          Ejecutable compilado que corre como <strong>servicio de Windows</strong> en lugar de tareas
          programadas. <strong>Se actualiza solo</strong> y verifica la firma del binario antes de
          adoptarlo. Cada equipo obtiene una <strong>credencial propia</strong>, revocable
          individualmente desde su ficha en el inventario.
        </p>

        {!binInfo?.disponible ? (
          <div style={{ padding: '12px 14px', background: 'var(--amber-dim)', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: 'var(--amber)' }}>
            El agente binario todavía no está disponible en este servidor. Por ahora usá el
            <strong> agente PowerShell</strong> (más abajo); el binario se habilitará próximamente.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
              <button onClick={downloadBinary} disabled={downloadingBin}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', background: 'var(--ink)', border: 'none', borderRadius: 8, color: 'var(--surface)', fontSize: 13, fontWeight: 600, cursor: downloadingBin ? 'wait' : 'pointer' }}>
                <Download size={15} /> {downloadingBin ? 'Descargando…' : `Descargar ITILAgent.exe`}
              </button>
              <a href={`${serverUrl}/agents/windows-net/install-service.ps1`} download
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--ink-2)', fontSize: 13, textDecoration: 'none' }}>
                <Terminal size={14} /> install-service.ps1
              </a>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
              Equipo nuevo (sin agente)
            </div>
            <ol style={{ fontSize: 13, color: '#444', paddingLeft: 20, margin: '0 0 8px', lineHeight: 1.7 }}>
              <li>Instalar primero el agente PowerShell (crea el <code>config.json</code> con la URL y el token).</li>
              <li>Descargar <code>ITILAgent.exe</code> y <code>install-service.ps1</code> en la misma carpeta.</li>
              <li>Abrir PowerShell <strong>como Administrador</strong> y ejecutar:</li>
            </ol>
            <CodeBlock code={`.\\install-service.ps1 -ExePath .\\ITILAgent.exe`} id="binNew" copied={copied} onCopy={copy} />

            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', margin: '20px 0 8px' }}>
              Equipo que ya tiene el agente PowerShell
            </div>
            <p style={{ fontSize: 13, color: '#444', margin: '0 0 8px', lineHeight: 1.6 }}>
              No hace falta tocar el equipo: se migra en <strong>dos fases</strong> desde
              Automatización, con los scripts <em>Migración a agente binario</em>. La fase 1 instala el
              servicio y <strong>deja vivo el agente PowerShell</strong>; recién cuando se confirma que
              el binario reporta, la fase 2 lo desinstala.
            </p>
            <div style={{ padding: '10px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
              Durante la convivencia los dos agentes reportan y la versión del equipo alterna entre
              1.x y 2.x: es esperable y se normaliza al completar la fase 2. Si el binario no arranca,
              el agente PowerShell sigue gestionando el equipo.
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', margin: '20px 0 8px' }}>
              Instalación manual sin quitar el agente PowerShell
            </div>
            <CodeBlock code={`.\\install-service.ps1 -ExePath .\\ITILAgent.exe -KeepPsAgent`} id="binKeep" copied={copied} onCopy={copy} />
          </>
        )}
      </div>

      {/* Windows */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Monitor size={20} style={{ color: '#0078D4' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Windows — agente PowerShell</h2>
          <span style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>
            PowerShell · Task Scheduler
          </span>
        </div>

        <p style={{ fontSize: 14, color: '#444', marginBottom: 20 }}>
          Compatible con <strong>Windows 10/11</strong> y <strong>Windows Server 2016+</strong>.
          Requiere PowerShell 5.1+ y ejecutarse como <strong>Administrador</strong>.
        </p>

        {/* Opción A */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Opción A — Un solo comando (descarga e instala)
          </div>
          <CodeBlock code={winCmdOne} id="winA" copied={copied} onCopy={copy} />
        </div>

        {/* Opción B */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Opción B — Descarga manual + instalación
          </div>
          <ol style={{ fontSize: 13, color: '#444', paddingLeft: 20, margin: '0 0 10px' }}>
            <li style={{ marginBottom: 4 }}>
              Descargá:{' '}
              <a href={`${serverUrl}/agents/windows/install-agent.ps1`} style={{ color: 'var(--accent)' }}>install-agent.ps1</a>
              {' '}y{' '}
              <a href={`${serverUrl}/agents/windows/itil-agent.ps1`} style={{ color: 'var(--accent)' }}>itil-agent.ps1</a>
              {' '}en la misma carpeta
            </li>
            <li>Abrí PowerShell como Administrador y ejecutá:</li>
          </ol>
          <CodeBlock code={winCmdManual} id="winB" copied={copied} onCopy={copy} />
        </div>

        {/* Qué hace */}
        <div style={{ background: '#F0FFF4', border: '1px solid #86EFAC', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> El instalador</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--green)', lineHeight: 1.7 }}>
            <li>Verifica conectividad y token antes de instalar</li>
            <li>Crea la tarea en <code style={{ background: 'var(--green-dim)', padding: '1px 4px', borderRadius: 3 }}>Administrador de tareas → \ITILSuite\ITILAgent</code></li>
            <li>Ejecuta el primer reporte inmediatamente</li>
            <li>Detecta si el equipo es físico o virtual (VMware, Hyper-V, VirtualBox, KVM)</li>
            <li>Logs en <code style={{ background: 'var(--green-dim)', padding: '1px 4px', borderRadius: 3 }}>C:\ProgramData\ITILAgent\agent.log</code></li>
            <li>Para desinstalar: <code style={{ background: 'var(--green-dim)', padding: '1px 4px', borderRadius: 3 }}>C:\ProgramData\ITILAgent\uninstall-agent.ps1</code></li>
          </ul>
        </div>
      </div>

      {/* Linux — agente binario */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <Terminal size={20} style={{ color: 'var(--accent)' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Linux — agente binario</h2>
          <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 700 }}>Recomendado</span>
          <span style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>Binario estático · cron</span>
        </div>

        <p style={{ fontSize: 14, color: '#444', marginBottom: 16 }}>
          Ejecutable compilado, <strong>sin dependencias externas</strong> — no requiere <code>curl</code>, <code>jq</code> ni <code>openssl</code>
          en el sistema. Las claves ECDSA de verificación de comandos están compiladas en el binario y no son visibles en disco.
        </p>

        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
          Un solo comando (descarga, configura e instala)
        </div>
        <CodeBlock
          code={`curl -sf ${serverUrl}/agents/linux-go/install-agent-bin.sh | sudo bash -s -- --api-url "${serverUrl}" --org-token "${token}"`}
          id="linBinOne"
          copied={copied}
          onCopy={copy}
        />

        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', margin: '20px 0 8px' }}>
          Descarga manual
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href={`${serverUrl}/agents/linux-go/install-agent-bin.sh`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--ink-2)', fontSize: 13, textDecoration: 'none' }}>
            <Terminal size={13} /> install-agent-bin.sh
          </a>
          <a href={`${serverUrl}/agents/linux-go/itil-agent`} download="itil-agent"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--ink-2)', fontSize: 13, textDecoration: 'none' }}>
            <Download size={13} /> itil-agent
          </a>
        </div>

        <div style={{ background: '#F0FFF4', border: '1px solid #86EFAC', borderRadius: 8, padding: 14, marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> El binario</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--green)', lineHeight: 1.7 }}>
            <li>Binario estático (5 MB), funciona en cualquier distro x86-64 sin instalar nada</li>
            <li>Detecta físico vs virtual (KVM, VMware, VirtualBox, Xen, AWS, GCP, Azure)</li>
            <li>Firma HMAC-SHA256 del reporte y verificación ECDSA de comandos C2</li>
            <li>Logs en <code style={{ background: 'var(--green-dim)', padding: '1px 4px', borderRadius: 3 }}>/var/log/itil-agent.log</code> con rotación automática a 5 MB</li>
            <li>Para desinstalar: <code style={{ background: 'var(--green-dim)', padding: '1px 4px', borderRadius: 3 }}>rm /usr/local/bin/itil-agent /etc/itil-agent/config.json /etc/cron.d/itil-agent</code></li>
          </ul>
        </div>
      </div>

      {/* Linux — agente Bash */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Terminal size={20} style={{ color: 'var(--amber)' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Linux — agente Bash</h2>
          <span style={{ color: 'var(--amber)', fontSize: 11, fontWeight: 600 }}>
            Bash · systemd timer
          </span>
        </div>

        <p style={{ fontSize: 14, color: '#444', marginBottom: 20 }}>
          Compatible con <strong>Ubuntu 20/22/24</strong>, <strong>Debian</strong>, <strong>RHEL/CentOS</strong>, <strong>Fedora</strong>, <strong>Arch</strong> y otras distribuciones.
          Requiere ejecutarse como <strong>root</strong> (sudo).
        </p>

        {/* Opción A */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Opción A — Un solo comando (descarga e instala)
          </div>
          <CodeBlock
            code={`curl -sf ${serverUrl}/agents/linux/install-agent.sh | sudo bash -s -- --api-url "${serverUrl}" --org-token "${token}"`}
            id="linA"
            copied={copied}
            onCopy={copy}
          />
        </div>

        {/* Opción B */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Opción B — Descarga manual + instalación
          </div>
          <ol style={{ fontSize: 13, color: '#444', paddingLeft: 20, margin: '0 0 10px' }}>
            <li style={{ marginBottom: 4 }}>
              Descargá el instalador:{' '}
              <a href={`${serverUrl}/agents/linux/install-agent.sh`} style={{ color: 'var(--accent)' }}>install-agent.sh</a>
            </li>
            <li>Ejecutá como root:</li>
          </ol>
          <CodeBlock
            code={`sudo bash install-agent.sh --api-url "${serverUrl}" --org-token "${token}"`}
            id="linB"
            copied={copied}
            onCopy={copy}
          />
        </div>

        {/* Qué hace */}
        <div style={{ background: 'var(--amber-dim)', border: '1px solid var(--amber-dim)', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--amber)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> El instalador</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--amber)', lineHeight: 1.7 }}>
            <li>Instala dependencias necesarias (<code style={{ background: 'var(--amber-dim)', padding: '1px 4px', borderRadius: 3 }}>curl</code>, <code style={{ background: 'var(--amber-dim)', padding: '1px 4px', borderRadius: 3 }}>jq</code>) si no están presentes</li>
            <li>Verifica conectividad y token antes de instalar</li>
            <li>Crea un <strong>systemd timer</strong> (o cron como alternativa) para ejecución periódica</li>
            <li>Ejecuta el primer reporte inmediatamente</li>
            <li>Detecta si el equipo es físico o virtual (KVM, VMware, VirtualBox, Xen, AWS, Azure, GCP)</li>
            <li>Logs en <code style={{ background: 'var(--amber-dim)', padding: '1px 4px', borderRadius: 3 }}>/var/log/itil-agent.log</code></li>
            <li>Para desinstalar: <code style={{ background: 'var(--amber-dim)', padding: '1px 4px', borderRadius: 3 }}>sudo systemctl disable --now itil-agent.timer && sudo rm /usr/local/bin/itil-agent.sh</code></li>
          </ul>
        </div>
      </div>

      {/* Android */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Smartphone size={20} style={{ color: 'var(--green)' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Android</h2>
          <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 600 }}>
            APK · WorkManager
          </span>
        </div>

        <p style={{ fontSize: 14, color: '#444', marginBottom: 20 }}>
          Compatible con <strong>Android 8.0+</strong> (API 26). Reporta inventario del dispositivo en segundo plano.
          Requiere permitir <strong>fuentes desconocidas</strong> para instalar el APK.
        </p>

        {/* Descarga directa */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 12 }}>
            Paso 1 — Descargar el APK
          </div>
          <a
            href={`${API_URL}/android-agent/download`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--surface)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
          >
            <Download size={15} />
            Descargar itil-agent.apk
          </a>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#888' }}>
            O compartir el link por WhatsApp/mail:{' '}
            <button className="btn btn-secondary btn-sm" onClick={() => copy(`${serverUrl}/api/android-agent/download`, 'apk-link')} style={{ marginLeft: 4 }}>
              {copied === 'apk-link' ? <Check size={12} /> : <Copy size={12} />}
              {copied === 'apk-link' ? ' Copiado' : ' Copiar link'}
            </button>
          </p>
        </div>

        {/* Instalación */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Paso 2 — Instalar
          </div>
          <ol style={{ fontSize: 13, color: '#444', paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
            <li>Abrir el APK descargado en el teléfono</li>
            <li>Si aparece advertencia: <strong>Ajustes → Instalar apps desconocidas</strong> → permitir</li>
            <li>Instalar y abrir la app <strong>del agente</strong></li>
          </ol>
        </div>

        {/* Configuración */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Paso 3 — Configurar en el teléfono
          </div>
          <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 0', color: 'var(--ink-2)', width: 120 }}>Servidor:</td>
                  <td><code style={{ background: 'var(--border)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{serverUrl}</code></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 0', color: 'var(--ink-2)' }}>Org Token:</td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <code style={{ background: 'var(--border)', padding: '2px 6px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>{token}</code>
                    <button className="btn btn-secondary btn-sm" onClick={() => copy(token, 'token-android')} style={{ flexShrink: 0 }}>
                      {copied === 'token-android' ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 0', color: 'var(--ink-2)' }}>Intervalo:</td>
                  <td><span style={{ fontSize: 13 }}>inventario {interval} min · comandos {cmdInterval} min (config global)</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Qué hace */}
        <div style={{ background: 'var(--green-dim)', border: '1px solid #86efac', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> El agente reporta</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--green)', lineHeight: 1.7 }}>
            <li>Fabricante, modelo, versión Android y build</li>
            <li>Dirección IP y red Wi-Fi conectada</li>
            <li>Estado de batería y almacenamiento</li>
            <li>RAM total y apps instaladas (no sistema)</li>
            <li>Seguridad: bloqueo de pantalla, cifrado, fuentes desconocidas</li>
            <li>Se ejecuta en segundo plano con WorkManager (sobrevive reinicios)</li>
          </ul>
        </div>
      </div>

    </>)}

    </div>
  );
}

function CodeBlock({ code, id, copied, onCopy }) {
  return (
    <div style={{ position: 'relative' }}>
      <pre style={{
        background: '#1E1E1E', color: '#D4D4D4', padding: '12px 48px 12px 16px',
        borderRadius: 8, fontSize: 12, fontFamily: 'monospace', margin: 0,
        overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6
      }}>
        {code}
      </pre>
      <button
        onClick={() => onCopy(code, id)}
        title="Copiar"
        style={{
          position: 'absolute', top: 8, right: 8,
          background: copied === id ? 'var(--green)' : 'var(--ink-2)',
          border: 'none', borderRadius: 6, padding: '6px 8px',
          cursor: 'pointer', color: 'var(--surface)', transition: 'background 0.2s'
        }}
      >
        {copied === id ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
